import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { ZAR, useCart } from "@/lib/cart";
import usePageMeta from "@/lib/useSeo";
import {
  ArrowRight,
  ArrowLeft,
  Sparkle,
  Confetti,
  Cigarette,
  Snowflake,
  Cherries,
  ShoppingBag,
} from "@phosphor-icons/react";

/**
 * Flavour Finder — a 4-question quiz that recommends 3 products and captures
 * an email at the end (opt-in newsletter). Pure client-side scoring — the
 * dataset never leaves the browser. Big content-marketing lead magnet.
 */

const QUESTIONS = [
  {
    id: "category",
    title: "What are you into?",
    subtitle: "Pick your poison.",
    options: [
      { value: "Disposable Vapes", label: "Vapes", Icon: Sparkle, tint: "#FF5A1F" },
      { value: "Nicotine Pouches", label: "Pouches", Icon: Cigarette, tint: "#00FFC2" },
      { value: "either", label: "Either / both", Icon: Confetti, tint: "#FFC900" },
    ],
  },
  {
    id: "flavour",
    title: "Flavour vibe?",
    subtitle: "Pick what makes your day.",
    options: [
      { value: "menthol", label: "Menthol / ice", Icon: Snowflake, tint: "#00FFC2", tags: ["ice", "mint", "menthol", "freeze", "cool"] },
      { value: "fruit", label: "Fruit / sweet", Icon: Cherries, tint: "#FF5A1F", tags: ["berry", "watermelon", "mango", "peach", "grape", "cherry", "razz", "fruit", "cola", "candy"] },
      { value: "tobacco", label: "Tobacco / classic", Icon: Cigarette, tint: "#FFC900", tags: ["tobacco", "classic", "citrus"] },
      { value: "surprise", label: "Surprise me", Icon: Confetti, tint: "#8B5CF6", tags: [] },
    ],
  },
  {
    id: "strength",
    title: "How much nic?",
    subtitle: "For pouches, we match strength. Vapes filter on high/low.",
    options: [
      { value: "light", label: "Light (≤ 6mg)", Icon: Sparkle, tint: "#00FFC2" },
      { value: "medium", label: "Regular (10-20mg)", Icon: Sparkle, tint: "#FFC900" },
      { value: "strong", label: "Strong (30-50mg+)", Icon: Sparkle, tint: "#FF5A1F" },
    ],
  },
  {
    id: "budget",
    title: "Budget?",
    subtitle: "We'll match your price range.",
    options: [
      { value: "cheap", label: "Under R100", Icon: ShoppingBag, tint: "#00FFC2", max: 100 },
      { value: "mid", label: "R100 – R200", Icon: ShoppingBag, tint: "#FFC900", max: 200 },
      { value: "any", label: "No limit", Icon: ShoppingBag, tint: "#FF5A1F", max: 9999 },
    ],
  },
];

export default function FlavourFinder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const cart = useCart();

  usePageMeta({
    title: "Flavour Finder Quiz · Vape Essence",
    description:
      "Answer 4 quick questions and we'll match you with 3 vapes or pouches you'll actually love. Free · 20 seconds.",
  });

  const currentQ = QUESTIONS[step];
  const totalSteps = QUESTIONS.length;

  const pick = (value) => {
    const next = { ...answers, [currentQ.id]: value };
    setAnswers(next);
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      runMatch(next);
    }
  };

  const runMatch = async (a) => {
    const { data: all } = await api.get("/products", { params: { limit: 100 } });
    const flavourOpt = QUESTIONS[1].options.find((o) => o.value === a.flavour);
    const budgetMax = QUESTIONS[3].options.find((o) => o.value === a.budget)?.max || 9999;

    const scored = all
      .filter((p) => p.stock > 0)
      .map((p) => {
        let s = 0;
        // Category
        if (a.category !== "either" && p.category !== a.category) s -= 5;
        else if (p.category === a.category) s += 4;
        // Flavour tags
        if (flavourOpt?.tags?.length) {
          const hay = `${p.name} ${p.description || ""} ${(p.tags || []).join(" ")}`.toLowerCase();
          if (flavourOpt.tags.some((t) => hay.includes(t))) s += 6;
        } else if (a.flavour === "surprise") {
          s += Math.random() * 4;
        }
        // Strength — look at variant options for a strength match
        const strengthText = JSON.stringify(p.variant_options || []).toLowerCase();
        if (a.strength === "light" && /(3mg|4mg|5mg|6mg|low)/.test(strengthText)) s += 3;
        if (a.strength === "medium" && /(10mg|12mg|15mg|18mg|20mg)/.test(strengthText)) s += 3;
        if (a.strength === "strong" && /(30mg|35mg|40mg|50mg|strong)/.test(strengthText)) s += 3;
        // Budget
        if (p.price <= budgetMax) s += 2;
        else s -= 4;
        // Featured tie-break
        if (p.featured) s += 1;
        return { p, s };
      })
      .sort((a, b) => b.s - a.s)
      .slice(0, 3)
      .map((x) => x.p);

    setResults(scored);
  };

  const restart = () => {
    setStep(0);
    setAnswers({});
    setResults(null);
    setEmail("");
    setSubscribed(false);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter/subscribe", { email });
      setSubscribed(true);
      toast.success("You're in — check your inbox for R50 off your first order.");
    } catch {
      toast.error("Couldn't subscribe. Try again in a moment.");
    }
  };

  const back = () => step > 0 && setStep(step - 1);

  return (
    <div className="mx-auto max-w-[820px] px-4 py-10 sm:px-6 sm:py-16 lg:px-10">
      <div className="font-label text-xs text-[#FFC900]">/ FLAVOUR FINDER</div>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tighter sm:text-5xl lg:text-6xl">
        Find your <span className="text-[#FF5A1F]">flavour</span>.
      </h1>
      <p className="mt-3 max-w-lg text-sm sm:text-base text-white/60">
        4 questions · 20 seconds · we match you with 3 hand-picked products.
      </p>

      {/* Progress */}
      {!results && (
        <div className="mt-8 sm:mt-10">
          <div className="flex items-center justify-between text-[10px] font-label text-white/40 mb-2">
            <span>QUESTION {step + 1} / {totalSteps}</span>
            <span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
          </div>
          <div className="h-1 w-full bg-white/10">
            <div
              className="h-full bg-gradient-to-r from-[#FF5A1F] to-[#00FFC2] transition-all duration-500"
              style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Question card */}
      <AnimatePresence mode="wait">
        {!results && (
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="mt-8 border border-white/10 bg-[#0d0d0d] p-5 sm:p-8"
          >
            <div className="font-display text-2xl font-black tracking-tighter sm:text-3xl">
              {currentQ.title}
            </div>
            <div className="mt-1 text-sm text-white/60">{currentQ.subtitle}</div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {currentQ.options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`quiz-option-${currentQ.id}-${opt.value}`}
                  onClick={() => pick(opt.value)}
                  className="group flex items-center gap-3 border border-white/15 bg-black p-4 text-left hover:border-[#FF5A1F] hover:-translate-y-0.5 transition-all"
                >
                  <div
                    className="inline-flex h-10 w-10 items-center justify-center border border-white/15 shrink-0"
                    style={{ color: opt.tint }}
                  >
                    <opt.Icon size={18} weight="duotone" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display text-base font-bold">{opt.label}</div>
                  </div>
                  <ArrowRight size={13} weight="bold" className="text-white/30 group-hover:text-[#FF5A1F] transition-colors" />
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                type="button"
                onClick={back}
                className="mt-5 inline-flex items-center gap-1 text-[11px] font-label text-white/50 hover:text-white transition-colors"
              >
                <ArrowLeft size={11} weight="bold" /> Back
              </button>
            )}
          </motion.div>
        )}

        {results && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Confetti size={16} weight="fill" className="text-[#FFC900]" />
              <div className="font-label text-[10px] tracking-widest text-[#FFC900]">
                / YOUR MATCHES
              </div>
            </div>
            <h2 className="font-display text-2xl font-black tracking-tighter sm:text-3xl">
              Try these three.
            </h2>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {results.length === 0 && (
                <div className="col-span-full text-sm text-white/50 border border-white/10 p-6">
                  Nothing matched all your criteria — try loosening one filter.
                </div>
              )}
              {results.map((p, i) => (
                <Link
                  key={p.slug}
                  to={`/product/${p.slug}`}
                  className="group block border border-white/10 bg-[#0d0d0d] hover:border-[#FF5A1F] transition-colors"
                >
                  <div className="relative aspect-square overflow-hidden bg-black">
                    {p.images?.[0] && (
                      <img
                        src={mediaUrl(p.images[0])}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    )}
                    <span
                      className="absolute left-2 top-2 px-2 py-[2px] font-label text-[9px] font-black tracking-widest text-black"
                      style={{ background: ["#FFC900", "#00FFC2", "#FF5A1F"][i] || "#FFC900" }}
                    >
                      #{i + 1} MATCH
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="font-label text-[9px] text-white/40 truncate">{p.brand}</div>
                    <div className="mt-1 font-display text-sm font-bold text-white line-clamp-1">
                      {p.name}
                    </div>
                    <div className="mt-1 font-display text-sm font-black text-[#00FFC2]">
                      {ZAR(p.price)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Email capture */}
            {!subscribed ? (
              <div className="mt-8 border border-[#FFC900]/40 bg-gradient-to-br from-[#1a1305] via-[#0d0d0d] to-[#0a1a15] p-5 sm:p-6">
                <div className="font-label text-[10px] tracking-widest text-[#FFC900]">
                  / UNLOCK R50 OFF YOUR FIRST ORDER
                </div>
                <div className="mt-2 font-display text-xl font-black tracking-tighter sm:text-2xl">
                  Get your matches by email + R50 off.
                </div>
                <p className="mt-2 text-sm text-white/60">
                  We'll email your top picks + a first-order discount code. Unsubscribe any time.
                </p>
                <form onSubmit={subscribe} className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="quiz-email-input"
                    className="flex-1 border border-white/15 bg-black px-4 py-3 text-sm outline-none focus:border-[#FF5A1F]"
                  />
                  <button
                    type="submit"
                    data-testid="quiz-email-submit"
                    className="bg-[#FF5A1F] px-5 py-3 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
                  >
                    UNLOCK R50 OFF →
                  </button>
                </form>
              </div>
            ) : (
              <div className="mt-8 border border-[#00FFC2]/40 bg-[#00FFC2]/5 p-5 sm:p-6 text-sm text-white/80">
                Boom — check your inbox for <b className="text-[#FFC900]">R50 OFF</b> your first order.
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={restart}
                className="border border-white/20 px-5 py-2.5 font-label text-xs hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
              >
                Retake quiz
              </button>
              <Link
                to="/shop"
                className="bg-[#00FFC2] px-5 py-2.5 font-label text-xs text-black hover:-translate-y-0.5 transition-transform"
              >
                Browse everything →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
